import { useState, useMemo, useEffect } from "react";
import api from "../../services/api";
import PhoneInput, { validatePhone, formatPhone } from "../../components/PhoneInput";

/* ── Helper components defined OUTSIDE to prevent focus loss ── */

function Section({ title, children }) {
  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 space-y-4">
      <h2 className="text-base font-semibold text-gray-800 border-b border-gray-100 pb-3">{title}</h2>
      {children}
    </div>
  );
}

function SaveBtn({ loading, onClick, label }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={loading}
      className="px-6 py-2.5 bg-green-600 text-white rounded-xl text-sm font-semibold hover:bg-green-700 disabled:opacity-60 transition"
    >
      {loading ? "Saving…" : label}
    </button>
  );
}

function InputField({ label, value, onChange, error, placeholder, type }) {
  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
      <input
        type={type || "text"}
        className={`w-full border rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 transition ${
          error ? "border-red-400 focus:ring-red-400" : "border-gray-200 focus:ring-green-400"
        }`}
        placeholder={placeholder || ""}
        value={value}
        onChange={e => onChange(e.target.value)}
        autoComplete="off"
      />
      {error && <p className="text-red-500 text-xs mt-1">{error}</p>}
    </div>
  );
}

function PwInputField({ label, value, onChange, error, placeholder, fieldShow, onToggle }) {
  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
      <div className="relative">
        <input
          type={fieldShow ? "text" : "password"}
          className={`w-full border rounded-xl px-4 py-2.5 pr-11 text-sm focus:outline-none focus:ring-2 transition ${
            error ? "border-red-400 focus:ring-red-400" : "border-gray-200 focus:ring-green-400"
          }`}
          placeholder={placeholder || ""}
          value={value}
          onChange={e => onChange(e.target.value)}
          autoComplete="new-password"
        />
        <button
          type="button"
          onClick={onToggle}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 text-lg leading-none"
          tabIndex={-1}
        >
          {fieldShow ? "🙈" : "👁️"}
        </button>
      </div>
      {error && <p className="text-red-500 text-xs mt-1">{error}</p>}
    </div>
  );
}

/* ── Main component ── */
export default function AdminSettings() {
  const admin = useMemo(() => {
    try { return JSON.parse(sessionStorage.getItem("adminUser") || "{}"); }
    catch { return {}; }
  }, []);

  /* ── Change Password ── */
  const [pwForm,    setPwForm]    = useState({ current: "", newPwd: "", confirm: "" });
  const [pwShow,    setPwShow]    = useState({ current: false, newPwd: false, confirm: false });
  const [pwErrors,  setPwErrors]  = useState({});
  const [pwMsg,     setPwMsg]     = useState("");
  const [pwLoading, setPwLoading] = useState(false);

  /* ── Profile ── */
  const [name,           setName]           = useState(admin.name || "");
  const [profileMsg,     setProfileMsg]     = useState("");
  const [profileLoading, setProfileLoading] = useState(false);
  const [profileError,   setProfileError]   = useState("");

  /* ── Change Email ── */
  const [newEmail,       setNewEmail]       = useState("");
  const [emailPwd,       setEmailPwd]       = useState("");
  const [emailErrors,    setEmailErrors]    = useState({});
  const [emailMsg,       setEmailMsg]       = useState("");
  const [emailLoading,   setEmailLoading]   = useState(false);
  const [showEmailPwd,   setShowEmailPwd]   = useState(false);

  /* ── Clinic Info ── */
  const [clinicName,    setClinicName]    = useState("");
  const [clinicPhone,   setClinicPhone]   = useState({ countryCode: "+91", number: "" });
  const [clinicAddress, setClinicAddress] = useState("");
  const [clinicMsg,     setClinicMsg]     = useState("");
  const [clinicLoading, setClinicLoading] = useState(false);
  const [clinicErrors,  setClinicErrors]  = useState({});

  /* ── Online Consultation ── */
  const [disclaimer,       setDisclaimer]       = useState("");
  const [consultFee,       setConsultFee]        = useState("");
  const [consultMsg,       setConsultMsg]        = useState("");
  const [consultLoading,   setConsultLoading]    = useState(false);
  const [consultFeeError,  setConsultFeeError]   = useState("");

  /* Load existing online consultation settings */
  useEffect(() => {
    api.get("/settings/online_consultation_disclaimer")
      .then(r => { if (r.data) setDisclaimer(r.data); })
      .catch(() => {});
    api.get("/settings/consultation_fee")
      .then(r => { if (r.data != null) setConsultFee(String(r.data)); })
      .catch(() => {});
  }, []);

  /* ── Password change ── */
  const validatePw = () => {
    const e = {};
    if (!pwForm.current)  e.current = "Current password is required";
    if (!pwForm.newPwd)   e.newPwd  = "New password is required";
    else if (pwForm.newPwd.length < 6) e.newPwd = "Minimum 6 characters";
    if (!pwForm.confirm)  e.confirm = "Please confirm your password";
    else if (pwForm.newPwd !== pwForm.confirm) e.confirm = "Passwords do not match";
    return e;
  };

  const changePassword = async () => {
    const errs = validatePw();
    setPwErrors(errs);
    setPwMsg("");
    if (Object.keys(errs).length > 0) return;
    setPwLoading(true);
    try {
      await api.put("/admin/change-password", {
        currentPassword: pwForm.current,
        newPassword:     pwForm.newPwd,
      });
      setPwMsg("✅ Password changed successfully!");
      setPwForm({ current: "", newPwd: "", confirm: "" });
    } catch (err) {
      setPwErrors({ current: err.response?.data?.message || "Check your current password." });
    } finally {
      setPwLoading(false);
    }
  };

  /* ── Profile ── */
  const saveProfile = async () => {
    setProfileError(""); setProfileMsg("");
    if (!name.trim()) { setProfileError("Name is required"); return; }
    if (!/^[a-zA-Z\s'.\\-]+$/.test(name.trim())) { setProfileError("Letters only"); return; }
    setProfileLoading(true);
    try {
      await api.put("/admin/profile", { name: name.trim() });
      const updated = { ...admin, name: name.trim() };
      sessionStorage.setItem("adminUser", JSON.stringify(updated));
      setProfileMsg("✅ Profile updated!");
    } catch (err) {
      setProfileError(err.response?.data?.message || "Failed to update");
    } finally {
      setProfileLoading(false);
    }
  };

  /* ── Change Email ── */
  const changeEmail = async () => {
    const e = {};
    if (!newEmail.trim()) e.newEmail = "New email is required";
    else if (!/\S+@\S+\.\S+/.test(newEmail.trim())) e.newEmail = "Enter a valid email address";
    if (!emailPwd) e.emailPwd = "Current password is required";
    setEmailErrors(e); setEmailMsg("");
    if (Object.keys(e).length > 0) return;
    setEmailLoading(true);
    try {
      await api.put("/admin/change-email", { newEmail: newEmail.trim(), currentPassword: emailPwd });
      const updated = { ...admin, email: newEmail.trim().toLowerCase() };
      sessionStorage.setItem("adminUser", JSON.stringify(updated));
      setEmailMsg("✅ Email updated successfully!");
      setNewEmail(""); setEmailPwd("");
    } catch (err) {
      setEmailErrors({ newEmail: err.response?.data?.message || "Failed to update email" });
    } finally {
      setEmailLoading(false);
    }
  };

  /* ── Clinic info ── */
  const saveClinic = async () => {
    const e = {};
    const phoneErr = validatePhone(clinicPhone, false);
    if (phoneErr) e.clinicPhone = phoneErr;
    setClinicErrors(e); setClinicMsg("");
    if (Object.keys(e).length > 0) return;
    setClinicLoading(true);
    try {
      await api.post("/settings", { key: "clinicInfo", value: { clinicName, clinicPhone: formatPhone(clinicPhone), clinicAddress } });
      setClinicMsg("✅ Clinic info saved!");
    } catch {
      setClinicMsg("❌ Failed to save");
    } finally {
      setClinicLoading(false);
    }
  };

  /* ── Online consultation settings ── */
  const saveConsultSettings = async () => {
    setConsultFeeError(""); setConsultMsg("");
    const fee = Number(consultFee);
    if (!consultFee || isNaN(fee) || fee <= 0) {
      setConsultFeeError("Enter a valid fee amount in ₹");
      return;
    }
    setConsultLoading(true);
    try {
      await Promise.all([
        api.post("/settings", { key: "online_consultation_disclaimer", value: disclaimer }),
        api.post("/settings", { key: "consultation_fee",               value: fee }),
      ]);
      setConsultMsg("✅ Online consultation settings saved!");
    } catch {
      setConsultMsg("❌ Failed to save settings");
    } finally {
      setConsultLoading(false);
    }
  };

  const togglePwShow = (f) => setPwShow(p => ({ ...p, [f]: !p[f] }));
  const setPwField   = (f, v) => {
    setPwForm(p => ({ ...p, [f]: v }));
    if (pwErrors[f]) setPwErrors(p => ({ ...p, [f]: "" }));
  };

  return (
    <div className="space-y-6 max-w-2xl">

      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-800">⚙️ Settings</h1>
        <p className="text-sm text-gray-500 mt-1">Manage your account and system configuration</p>
      </div>

      {/* Account Info */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
        <h2 className="text-base font-semibold text-gray-800 border-b border-gray-100 pb-3 mb-4">Account Info</h2>
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 rounded-full bg-gradient-to-br from-green-500 to-emerald-600 flex items-center justify-center text-white text-xl font-bold shadow">
            {admin.name?.charAt(0)?.toUpperCase() || "A"}
          </div>
          <div>
            <p className="font-semibold text-gray-800">{admin.name}</p>
            <p className="text-sm text-gray-400">{admin.email || "—"}</p>
            <span className="inline-block mt-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-700">
              {admin.role}
            </span>
          </div>
        </div>
      </div>

      {/* Display Name */}
      <Section title="✏️ Update Display Name">
        <InputField label="Full Name" value={name}
          onChange={v => { setName(v); setProfileError(""); }}
          error={profileError} placeholder="Your display name"/>
        {profileMsg && <p className="text-sm text-green-600">{profileMsg}</p>}
        <SaveBtn loading={profileLoading} onClick={saveProfile} label="Update Name"/>
      </Section>

      {/* Change Email */}
      <Section title="📧 Change Email Address">
        <p className="text-xs text-gray-400 -mt-2 mb-2">
          Confirm your current password to update your login email.
        </p>
        <InputField label="New Email Address" value={newEmail} type="email"
          onChange={v => { setNewEmail(v); setEmailErrors(p => ({...p, newEmail: ""})); }}
          error={emailErrors.newEmail} placeholder="new@example.com"/>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Current Password</label>
          <div className="relative">
            <input
              type={showEmailPwd ? "text" : "password"}
              className={`w-full border rounded-xl px-4 py-2.5 pr-11 text-sm focus:outline-none focus:ring-2 transition ${
                emailErrors.emailPwd ? "border-red-400 focus:ring-red-400" : "border-gray-200 focus:ring-green-400"
              }`}
              placeholder="Your current password"
              value={emailPwd}
              onChange={e => { setEmailPwd(e.target.value); setEmailErrors(p => ({...p, emailPwd: ""})); }}
              autoComplete="current-password"
            />
            <button type="button" onClick={() => setShowEmailPwd(p => !p)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 text-lg leading-none" tabIndex={-1}>
              {showEmailPwd ? "🙈" : "👁️"}
            </button>
          </div>
          {emailErrors.emailPwd && <p className="text-red-500 text-xs mt-1">{emailErrors.emailPwd}</p>}
        </div>
        {emailMsg && <p className="text-sm text-green-600">{emailMsg}</p>}
        <SaveBtn loading={emailLoading} onClick={changeEmail} label="Update Email"/>
      </Section>

      {/* Change Password */}
      <Section title="🔒 Change Password">
        <PwInputField label="Current Password" value={pwForm.current}
          onChange={v => setPwField("current", v)} error={pwErrors.current}
          placeholder="Current password" fieldShow={pwShow.current}
          onToggle={() => togglePwShow("current")}/>
        <PwInputField label="New Password" value={pwForm.newPwd}
          onChange={v => setPwField("newPwd", v)} error={pwErrors.newPwd}
          placeholder="Minimum 6 characters" fieldShow={pwShow.newPwd}
          onToggle={() => togglePwShow("newPwd")}/>
        <PwInputField label="Confirm New Password" value={pwForm.confirm}
          onChange={v => setPwField("confirm", v)} error={pwErrors.confirm}
          placeholder="Repeat new password" fieldShow={pwShow.confirm}
          onToggle={() => togglePwShow("confirm")}/>
        {pwMsg && <p className="text-sm text-green-600">{pwMsg}</p>}
        <SaveBtn loading={pwLoading} onClick={changePassword} label="Change Password"/>
      </Section>

      {/* Clinic Info */}
      <Section title="🏥 Clinic Information">
        <p className="text-xs text-gray-400 -mt-2 mb-2">
          Shown on public pages and printed prescriptions.
        </p>
        <InputField label="Clinic / Hospital Name" value={clinicName}
          onChange={setClinicName} placeholder="e.g. City Health Clinic"/>
        <PhoneInput
          label="Contact Phone"
          value={clinicPhone}
          onChange={v => { setClinicPhone(v); setClinicErrors({}); }}
          error={clinicErrors.clinicPhone}
        />
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
          <textarea className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-green-400 transition"
            rows={2} placeholder="Clinic address" value={clinicAddress}
            onChange={e => setClinicAddress(e.target.value)}/>
        </div>
        {clinicMsg && (
          <p className={`text-sm ${clinicMsg.startsWith("✅") ? "text-green-600" : "text-red-500"}`}>
            {clinicMsg}
          </p>
        )}
        <SaveBtn loading={clinicLoading} onClick={saveClinic} label="Save Clinic Info"/>
      </Section>

      {/* ── ONLINE CONSULTATION SETTINGS ── */}
      <Section title="💻 Online Consultation Settings">
        <p className="text-xs text-gray-400 -mt-2 mb-1">
          Configure the disclaimer shown to patients before payment, and set the consultation fee.
        </p>

        {/* Fee */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Consultation Fee (₹)
          </label>
          <div className="relative">
            <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 text-sm font-semibold">₹</span>
            <input type="number" min="0"
              className={`w-full border rounded-xl pl-8 pr-4 py-2.5 text-sm focus:outline-none focus:ring-2 transition ${
                consultFeeError ? "border-red-400 focus:ring-red-400" : "border-gray-200 focus:ring-green-400"
              }`}
              placeholder="e.g. 500"
              value={consultFee}
              onChange={e => { setConsultFee(e.target.value); setConsultFeeError(""); }}
            />
          </div>
          {consultFeeError && <p className="text-red-500 text-xs mt-1">{consultFeeError}</p>}
          <p className="text-xs text-gray-400 mt-1">This amount is charged via Razorpay at booking.</p>
        </div>

        {/* Disclaimer — moved to Online Consultation settings */}
        <div className="bg-blue-50 border border-blue-200 rounded-xl px-4 py-3">
          <p className="text-sm font-semibold text-blue-700 mb-1">📋 Patient Disclaimers</p>
          <p className="text-xs text-blue-600 leading-relaxed">
            Disclaimers are now managed per request type (Online, Appointment, Enquiry)
            under <strong>Online Consultation → Patient Disclaimers</strong>.
          </p>
        </div>

        {consultMsg && (
          <p className={`text-sm ${consultMsg.startsWith("✅") ? "text-green-600" : "text-red-500"}`}>
            {consultMsg}
          </p>
        )}
        <SaveBtn loading={consultLoading} onClick={saveConsultSettings}
          label="Save Consultation Settings"/>
      </Section>

      {/* Session */}
      <Section title="🚪 Session">
        <p className="text-sm text-gray-500">Sign out of all active sessions.</p>
        <button type="button"
          onClick={() => {
            sessionStorage.removeItem("adminToken");
            sessionStorage.removeItem("adminUser");
            window.location.href = "/clinic-management/signin";
          }}
          className="px-5 py-2.5 bg-red-50 text-red-600 border border-red-200 rounded-xl text-sm font-semibold hover:bg-red-100 transition">
          🚪 Sign Out
        </button>
      </Section>

    </div>
  );
}
